import {IElement} from '../incase-domain/elements';
import {ELEMENT} from '../incase-domain/elements/base/ElementTypes';
import {uploadImageFile, uploadVideoFile} from '../service/files.service';
import {FLIP_CARD_SIDE, IFlipCardElements} from '../incase-domain/elements/flipCard';
import {isManagedMediaUrl, replaceHtmlMedia, validateHtmlImageSizes} from './htmlEmbeddedImages';
import {dataUrlToFile} from './fileUtils';
import {validateImageSize} from './imageUploadValidation';

function isDataUrl(src: string): boolean {
    return src.startsWith('data:');
}

async function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read media blob'));
        reader.readAsDataURL(blob);
    });
}

async function convertSrcToBase64(src: string | undefined | null): Promise<string | undefined | null> {
    if (!src) {
        return src;
    }
    if (isDataUrl(src) || !isManagedMediaUrl(src)) {
        return src;
    }
    const response = await fetch(src, {credentials: 'include'});
    if (!response.ok) {
        throw new Error(`Failed to fetch media (${response.status})`);
    }
    return blobToDataUrl(await response.blob());
}

function extensionForMime(mimeType: string): string {
    if (mimeType === 'image/jpeg') return '.jpg';
    if (mimeType === 'image/png') return '.png';
    if (mimeType === 'image/gif') return '.gif';
    if (mimeType === 'image/webp') return '.webp';
    if (mimeType === 'video/mp4') return '.mp4';
    if (mimeType === 'video/webm') return '.webm';
    const subtype = mimeType.split('/')[1];
    return subtype ? `.${subtype}` : '';
}

async function convertBase64ToFileUrl(src: string | undefined | null): Promise<string | undefined | null> {
    if (!src || !isDataUrl(src)) {
        return src;
    }
    const mimeMatch = src.slice(0, src.indexOf(',')).match(/^data:([^;,]+)/);
    const mimeType = mimeMatch?.[1] ?? 'application/octet-stream';
    const file = dataUrlToFile(src, `element-import${extensionForMime(mimeType)}`);
    if (mimeType.startsWith('video/')) {
        return (await uploadVideoFile(file)).url;
    }
    validateImageSize(file.size);
    return (await uploadImageFile(file)).url;
}

type MediaConverter = (src: string | undefined | null) => Promise<string | undefined | null>;

async function mapFlipCardMedia(el: IFlipCardElements, convertSrc: MediaConverter): Promise<IFlipCardElements> {
    switch (el.type) {
        case ELEMENT.IMAGE:
            return {...el, imageSrc: (await convertSrc(el.imageSrc)) ?? el.imageSrc};
        case ELEMENT.HTML:
            return {...el, text: await replaceHtmlMedia(el.text, convertSrc)};
        default:
            return el;
    }
}

async function mapElementMediaSrcs(el: IElement, convertSrc: MediaConverter): Promise<IElement> {
    switch (el.type) {
        case ELEMENT.IMAGE:
        case ELEMENT.IMAGE_MARKUP:
            return {...el, imageSrc: (await convertSrc(el.imageSrc)) ?? el.imageSrc};
        case ELEMENT.VIDEO:
            return {
                ...el,
                ...(el.videoSource && {
                    videoSource: {...el.videoSource, src: (await convertSrc(el.videoSource.src)) ?? el.videoSource.src},
                }),
                ...(typeof el.videoSrc === 'string' && {videoSrc: (await convertSrc(el.videoSrc)) ?? el.videoSrc}),
            };
        case ELEMENT.IMAGE_GALLERY:
            return {
                ...el,
                images: await Promise.all(
                    el.images.map(async (image) => ({
                        ...image,
                        imageSrc: (await convertSrc(image.imageSrc)) ?? image.imageSrc,
                    }))
                ),
            };
        case ELEMENT.HTML:
            return {...el, text: await replaceHtmlMedia(el.text, convertSrc)};
        case ELEMENT.FLIP_CARD: {
            const [front, back] = await Promise.all([
                mapFlipCardMedia(el.flipCardElement[FLIP_CARD_SIDE.FRONT], convertSrc),
                mapFlipCardMedia(el.flipCardElement[FLIP_CARD_SIDE.BACK], convertSrc),
            ]);
            return {...el, flipCardElement: {[FLIP_CARD_SIDE.FRONT]: front, [FLIP_CARD_SIDE.BACK]: back}};
        }
        case ELEMENT.PHOTO_ANSWER_QUESTION:
            return {
                ...el,
                answers: await Promise.all(
                    el.answers.map(async (answer) => ({
                        ...answer,
                        imgSrc: (await convertSrc(answer.imgSrc)) ?? undefined,
                    }))
                ),
            };
        default:
            return el;
    }
}

export function convertElementMediaToBase64(el: IElement): Promise<IElement> {
    return mapElementMediaSrcs(el, convertSrcToBase64);
}

export async function convertElementBase64ToFileUrls(el: IElement): Promise<IElement> {
    if (el.type === ELEMENT.HTML) validateHtmlImageSizes(el.text);
    if (el.type === ELEMENT.FLIP_CARD) {
        Object.values(el.flipCardElement).forEach((side) => {
            if (side.type === ELEMENT.HTML) validateHtmlImageSizes(side.text);
        });
    }
    return mapElementMediaSrcs(el, convertBase64ToFileUrl);
}
